import React from 'react';
import { Check, Clock } from 'lucide-react';
import { DbsCheckStatus } from '../../services/api';

interface Stage {
    status: DbsCheckStatus;
    label: string;
}

const STAGES: Stage[] = [
    { status: 'link_sent',            label: 'Invite sent'         },
    { status: 'awaiting_initiation',  label: 'Identity verified'   },
    { status: 'form_ready',           label: 'DBS submitted'       },
    { status: 'form_complete',        label: 'Awaiting countersign' },
    { status: 'app_sent',             label: 'Sent to DBS'         },
    { status: 'app_received',         label: 'Received by DBS'     },
    { status: 'app_complete',         label: 'Result available'    },
];

const STATUS_ORDER: Partial<Record<DbsCheckStatus, number>> = {
    not_started:          -1,
    link_sent:             0,
    awaiting_initiation:   1,
    form_ready:            2,
    form_invalid:          2,
    form_complete:         3,
    form_authorised:       3,
    app_sent:              4,
    app_received:          5,
    app_rejected:          5,
    app_withdrawn:         5,
    app_complete:          6,
};

interface DbsStatusTimelineProps {
    status: DbsCheckStatus;
    isRejected?: boolean;
}

const DbsStatusTimeline: React.FC<DbsStatusTimelineProps> = ({ status, isRejected }) => {
    const rawIndex = STATUS_ORDER[status] ?? -1;
    const currentIndex = status === 'app_complete' ? STAGES.length : rawIndex;

    return (
        <div className="flex items-start w-full">
            {STAGES.map((stage, i) => {
                const isComplete = i < currentIndex;
                const isCurrent = i === currentIndex;
                const isFailed = isCurrent && (isRejected || status === 'app_rejected' || status === 'app_withdrawn' || status === 'form_invalid');

                return (
                    <React.Fragment key={stage.status}>
                        {/* Step */}
                        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                            <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                                    isComplete
                                        ? 'bg-green-500'
                                        : isCurrent && !isFailed
                                            ? 'border-2 bg-white'
                                            : isFailed
                                                ? 'bg-red-500'
                                                : 'border-2 border-gray-200 bg-white'
                                }`}
                                style={isCurrent && !isFailed ? { borderColor: '#69BE28' } : {}}
                            >
                                {isComplete && <Check className="w-3.5 h-3.5 text-white" />}
                                {isCurrent && !isFailed && <Clock className="w-3 h-3" style={{ color: '#69BE28' }} />}
                            </div>
                            <span className={`text-xs text-center leading-tight max-w-[72px] ${
                                isComplete
                                    ? 'text-gray-400'
                                    : isCurrent && !isFailed
                                        ? 'font-semibold text-gray-900'
                                        : isFailed
                                            ? 'font-semibold text-red-600'
                                            : 'text-gray-300'
                            }`}>
                                {stage.label}
                            </span>
                        </div>

                        {/* Connector */}
                        {i < STAGES.length - 1 && (
                            <div className={`flex-1 h-0.5 mt-3 ${i < currentIndex ? 'bg-green-500' : 'bg-gray-200'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default DbsStatusTimeline;
